"use client";

import AnswerSummary from "@/components/AnswerSummary";
import { useState, type ReactNode } from "react";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { OfferPlacement } from "@/components/OfferPlacement";
import { numerologyReportOffer } from "@/components/numerology-report-offer";
import RatingPrompt from "@/components/RatingPrompt";
import { RESULT_REASONS } from "@/lib/feedback/resultReasons";
import { calculateNameNumber } from "@/lib/api/numerology";
import { Collapsible } from "@ishvaram/ui/Collapsible";
import { ScoreBadge } from "@ishvaram/ui/ScoreBadge";
import { NAME_NUMEROLOGY_HEADLINES, PROFILES } from "./NameNumerology.data";
import { NameForm } from "./NameNumerology.form";
import { NameResult } from "./NameNumerology.result";
import { NAME_NUMEROLOGY_COPY } from "./NameNumerology.copy";
import { localizedProfile } from "./NameNumerology.data.hi";

export function NameNumerology({
  language = "en",
  sourcePath,
  crossLinks,
  answer,
}: {
  language?: "en" | "hi";
  sourcePath: string;
  crossLinks: ReactNode;
  /**
   * The AEO direct answer, composed on the SERVER and passed in as a string so
   * the answer module and its reference datasets stay out of the client bundle.
   * It renders directly under the <h1> this component owns and above the form,
   * because an extractor reading the first 40-60 words otherwise gets English
   * input chrome — on a Hindi page, for `/hi/name-numerology/`.
   */
  answer?: string;
}) {
  const [name, setName] = useState("");
  const [result, setResult] = useState<{ nameNumber: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = NAME_NUMEROLOGY_COPY[language];

  async function handleCalculate(e: React.FormEvent) {
    e.preventDefault();
    if (name.trim().length === 0 || loading) return;
    setLoading(true);
    setError(null);
    try {
      // Chaldean name number from the engine — same source of truth as
      // /numerology/, so the two pages never disagree on a name's number.
      const res = await calculateNameNumber(name.trim());
      setResult({ nameNumber: res.name_number.value });
      trackCalculatorUsed({ calculator: "name-numerology" });
    } catch {
      setError(copy.error);
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setName("");
    setResult(null);
    setError(null);
  }

  const found = result ? PROFILES.find((p) => p.number === result.nameNumber) : null;
  const profile = found ? localizedProfile(found, language) : null;

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12 sm:py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3 text-intent-brand">
            {copy.eyebrow}
          </p>
          <h1 className="text-[28px] sm:text-[36px] font-bold leading-tight text-ish-dark font-serif">
            {NAME_NUMEROLOGY_HEADLINES[language]}
          </h1>
          <p className="mt-3 text-body leading-relaxed max-w-lg mx-auto text-ish-muted">
            {copy.intro}
          </p>
        </div>

        {answer ? <AnswerSummary answer={answer} /> : null}

        {!result && (
          <NameForm name={name} onNameChange={setName} onSubmit={handleCalculate} copy={copy} />
        )}

        {loading && <p className="text-center text-body mt-4 text-ish-muted">{copy.calculating}</p>}
        {error && (
          <p className="text-center text-body mt-4 text-warning" role="alert">
            {error}
          </p>
        )}

        {result && profile && (
          <NameResult
            name={name}
            result={result}
            profile={profile}
            copy={copy}
            resultOffer={
              <OfferPlacement {...numerologyReportOffer({ language, sourcePath, name })} />
            }
            onReset={handleReset}
          />
        )}

        {result && profile && (
          <div className="mt-6">
            <RatingPrompt
              source={"tool_result" as never}
              mode="thumbs+reasons"
              reasons={RESULT_REASONS}
              prompt={copy.ratingPrompt}
              language={language}
              context={{
                surface: "name_numerology_result",
                path: typeof window !== "undefined" ? window.location.pathname : null,
              }}
            />
          </div>
        )}

        {/* All 9 Number Profiles (always visible for SEO) */}
        <Collapsible title={copy.quickReference} defaultOpen={false} headingLevel="h2">
          <div className="card-sacred p-6 sm:p-8 mt-0 space-y-4">
            {PROFILES.map((base) => localizedProfile(base, language)).map((p, idx) => (
              <div
                key={idx}
                className={`flex gap-4 items-start rounded-lg p-4 border ${
                  result?.nameNumber === idx + 1
                    ? "bg-ish-accent/10 border-ish-accent"
                    : "border-ish-border bg-transparent"
                }`}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-[18px] font-bold bg-ish-accent/10 text-intent-brand">
                  {idx + 1}
                </div>
                <div className="min-w-0">
                  <h3 className="text-body font-bold text-ish-dark">
                    {p.title} — {p.planet}
                  </h3>
                  <p className="text-body-small mt-1 text-ish-muted">
                    {p.characteristics[0]}. {p.characteristics[1]}.
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-caption text-ish-faint">{copy.compatibility}</span>
                    <ScoreBadge score={p.compatible.length} max={9} size="sm" />
                    <span className="text-caption text-ish-faint ml-2">
                      {copy.luckyColor} {p.lucky_color}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Collapsible>

        {crossLinks}
      </div>
    </div>
  );
}
